import { memo, useCallback, useRef } from 'react';
import type { ResourceResponse } from 'types';
import { useTypedSelector } from 'hooks';
import { PaymentProduct } from 'consts/openBanking';
import { SetupIdentifier } from 'consts/setups';
import { when } from 'util/support';
import { isFormChanged } from 'util/forms';
import type { BankingSdkConnectorsRecipientsPivot } from 'features/openBanking/types';
import {
    selectIsConstantsLoading,
    selectPaymentProducts
} from 'features/general/selectors';
import { selectIsSetupsLoading } from 'features/setups/selectors';
import { useBankingSDKConnectorsRecipientsPivotActions } from 'features/openBanking/actions';
import ActionPanel from 'ui/molecules/ActionPanel';
import When from 'ui/support/When';
import { BankingSDKConnectorsRecipientsPivot } from 'ui/forms/OpenBanking';
import {
    useResourceContext,
    ResourceDetail,
    type ResourceDetailProps
} from "ui/widgets/Resource";

const BankingSDKConnectorsRecipientsPivotDetail = (props: ResourceDetailProps) => {
    const { resource, onClose } = useResourceContext<BankingSdkConnectorsRecipientsPivot>();
    const resourceRef = useRef(resource);
    resourceRef.current = resource;

    return (
        <ResourceDetail
            {...props}
            disableCloseOnBackdropClick
            onClose={onClose}
        >
            <When
                if={Boolean(resource)}
                then={useCallback(() => (
                    <BankingSDKConnectorsRecipientsPivotForm
                        resource={resourceRef.current}
                    />
                ), [])}
            />
        </ResourceDetail>
    );
};

export default memo(BankingSDKConnectorsRecipientsPivotDetail);

function BankingSDKConnectorsRecipientsPivotForm({
    resource
}: {
    readonly resource: BankingSdkConnectorsRecipientsPivot;
}) {
    const isConstantsLoading = useTypedSelector(selectIsConstantsLoading);
    const isSetupsLoading = useTypedSelector(selectIsSetupsLoading(SetupIdentifier.Mors));

    const { onClose } = useResourceContext<BankingSdkConnectorsRecipientsPivot>();
    const {
        updateBankingSDKConnectorsRecipientsPivot
    } = useBankingSDKConnectorsRecipientsPivotActions();

    const formBootstrapData = {
        PaymentProducts: useTypedSelector(selectPaymentProducts)
    };

    const formProps: BankingSDKConnectorsRecipientsPivot.BankingSDKConnectorsRecipientsPivotForm = {
        isBootstrapDataLoading: (
            isConstantsLoading ||
            isSetupsLoading
        ),
        enableReinitialize: false,
        bootstrapData: formBootstrapData,
        initialValues: BankingSDKConnectorsRecipientsPivot.getInitialValues(resource, formBootstrapData),
        onSaveOrCreate: async ({
            regReportingCodes,
            paymentProduct,
            ...values
        }, formHelpers) => {
            const [request, result] = updateBankingSDKConnectorsRecipientsPivot({
                ...values,
                paymentProduct: Number(
                    paymentProduct?.id ||
                    PaymentProduct.Domestic
                ),
                regReportingCodes: when(
                    regReportingCodes,
                    JSON.stringify(regReportingCodes),
                    undefined
                )
            });

            const response = (await request) as ResourceResponse<BankingSdkConnectorsRecipientsPivot>;
            when(response.success, () => {
                onClose();
                formHelpers!.resetForm();
            });

            return result;
        }
    };

    const form = BankingSDKConnectorsRecipientsPivot.useForm(formProps);

    return (
        <BankingSDKConnectorsRecipientsPivot.Form
            {...form}
            {...formProps}
        >
            <ActionPanel
                isLoading={form.isSubmitting}
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || !isFormChanged(form),
                    loading: form.isSubmitting
                }}
            />
        </BankingSDKConnectorsRecipientsPivot.Form>
    );
}
